import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { certificatesApi, type CertificateListFilters } from './service';
import type {
  CreateCertificateInput,
  RevokeCertificateInput,
} from './schema';

/**
 * Query key factory. Lists are parameterised by parent (company,
 * vessel, equipment) + filters so different scopes stay cached
 * independently. The detail and "expiring" lists each get their
 * own subtree under all.
 *
 *   certificatesKeys.all                                 every cached query
 *   certificatesKeys.detail(id)                          single cert
 *   certificatesKeys.byCompany(id, filters?)             company list
 *   certificatesKeys.byVessel(id, filters?)              vessel list
 *   certificatesKeys.byEquipment(id, filters?)           equipment list
 *   certificatesKeys.expiringByCompany(id, days?)        expiring list
 */
export const certificatesKeys = {
  all: ['certificates'] as const,
  detail: (id: string) => [...certificatesKeys.all, 'detail', id] as const,
  byCompany: (companyId: string, filters: CertificateListFilters = {}) =>
    [...certificatesKeys.all, 'by-company', companyId, filters] as const,
  byVessel: (vesselId: string, filters: CertificateListFilters = {}) =>
    [...certificatesKeys.all, 'by-vessel', vesselId, filters] as const,
  byEquipment: (
    equipmentId: string,
    filters: CertificateListFilters = {},
  ) =>
    [...certificatesKeys.all, 'by-equipment', equipmentId, filters] as const,
  expiringByCompany: (companyId: string, days?: number) =>
    [
      ...certificatesKeys.all,
      'expiring-by-company',
      companyId,
      days ?? null,
    ] as const,
};

export function useCertificate(id: string | undefined) {
  return useQuery({
    queryKey: id ? certificatesKeys.detail(id) : certificatesKeys.all,
    queryFn: () => certificatesApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useCertificatesByCompany(
  companyId: string | undefined,
  filters: CertificateListFilters = {},
) {
  return useQuery({
    queryKey: companyId
      ? certificatesKeys.byCompany(companyId, filters)
      : certificatesKeys.all,
    queryFn: () => certificatesApi.listByCompany(companyId as string, filters),
    enabled: Boolean(companyId),
  });
}

export function useCertificatesByVessel(
  vesselId: string | undefined,
  filters: CertificateListFilters = {},
) {
  return useQuery({
    queryKey: vesselId
      ? certificatesKeys.byVessel(vesselId, filters)
      : certificatesKeys.all,
    queryFn: () => certificatesApi.listByVessel(vesselId as string, filters),
    enabled: Boolean(vesselId),
  });
}

export function useCertificatesByEquipment(
  equipmentId: string | undefined,
  filters: CertificateListFilters = {},
) {
  return useQuery({
    queryKey: equipmentId
      ? certificatesKeys.byEquipment(equipmentId, filters)
      : certificatesKeys.all,
    queryFn: () =>
      certificatesApi.listByEquipment(equipmentId as string, filters),
    enabled: Boolean(equipmentId),
  });
}

export function useExpiringCertificates(
  companyId: string | undefined,
  days?: number,
) {
  return useQuery({
    queryKey: companyId
      ? certificatesKeys.expiringByCompany(companyId, days)
      : certificatesKeys.all,
    queryFn: () =>
      certificatesApi.listExpiringByCompany(companyId as string, days),
    enabled: Boolean(companyId),
  });
}

/**
 * After a write, invalidate every list scope that could contain the
 * affected cert plus its detail. Done in a single helper so the
 * three mutation hooks stay terse.
 */
function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: certificatesKeys.all });
}

export function useCreateCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCertificateInput) => certificatesApi.create(input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useRevokeCertificate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RevokeCertificateInput) =>
      certificatesApi.revoke(id, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => certificatesApi.remove(id),
    onSuccess: () => invalidateAll(qc),
  });
}
